import { useNavigate } from 'react-router-dom';
import SidebarLogo from '../Root/SideBarLogo';
import { useGlobalHeaderStyles } from './styles/global-header.styles';
import { GlobalSearch } from '@internal/plugin-search';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import Settings from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import { LinkButton } from '@backstage/core-components';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { useEffect, useRef, useState } from 'react';
import { NotificationsHeaderItem } from '@internal/plugin-user-settings';

import { HeaderMenu } from './HeaderMenu';
import {
  SUBMENU_OPTIONS,
  submenuOptions,
  SUPPORT_SIDEBAR_OPTIONS,
} from '../sidebar-submenu';
import { SETTING_SIDEBAR_OPTIONS } from '../sidebar-submenu';
import { McpChatHeaderItem } from '@internal/backstage-plugin-mcp-chat';
import { RequireFeatureFlag } from '../RequireFeatureFlag';
import { mcpChatAccessPermission } from '@internal/plugin-permissions-common';

export const GlobalHeader = () => {
  const classes = useGlobalHeaderStyles();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState<submenuOptions>('none');
  const createButtonRef = useRef<HTMLButtonElement>(null);
  const createMenuRef = useRef<HTMLDivElement>(null);
  const supportButtonRef = useRef<HTMLButtonElement>(null);
  const supportMenuRef = useRef<HTMLDivElement>(null);
  const settingButtonRef = useRef<HTMLButtonElement>(null);
  const settingMenuRef = useRef<HTMLDivElement>(null);

  const { loading: loadingPermission, allowed: canCreateEntity } =
    usePermission({
      permission: catalogEntityCreatePermission,
    });
  const { loading: loadingDevtoolsPermission, allowed: canAllowDevtools } =
    usePermission({
      permission: devToolsAdministerPermission,
    });
  // Members of aiusers and admins, as the custom permission policy decides
  const { allowed: canUseXpert } = usePermission({
    permission: mcpChatAccessPermission,
  });

  const handleCreateClick = () => {
    navigate('/create-hub/management');
  };

  const handleHelpClick = () => {
    setActiveMenu(prev =>
      prev === SUBMENU_OPTIONS.support
        ? SUBMENU_OPTIONS.none
        : SUBMENU_OPTIONS.support,
    );
  };
  const handleSettingClick = () => {
    setActiveMenu(prev =>
      prev === SUBMENU_OPTIONS.setting
        ? SUBMENU_OPTIONS.none
        : SUBMENU_OPTIONS.setting,
    );
  };
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (
      !createButtonRef.current?.contains(target) &&
      !createMenuRef.current?.contains(target) &&
      !supportButtonRef.current?.contains(target) &&
      !supportMenuRef.current?.contains(target) &&
      !settingButtonRef.current?.contains(target) &&
      !settingMenuRef.current?.contains(target)
    ) {
      setActiveMenu(SUBMENU_OPTIONS.none);
    }
  };

  useEffect(() => {
    // Attach event listener for handling outside clicks to close the header menu
    document.addEventListener('mousedown', handleClickOutside);
    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className={classes.headerContainer} data-id="global-header">
        <div className={classes.logoContainer}>
          <SidebarLogo />
        </div>
        <div className={classes.searchContainer}>
          <GlobalSearch />
          {canUseXpert && (
            <RequireFeatureFlag flag="enable-ai-chat" errorPage={<></>}>
              <McpChatHeaderItem />
            </RequireFeatureFlag>
          )}
        </div>
        <div className={classes.actionContainer}>
          {!loadingPermission && canCreateEntity && (
            <div className={classes.commonContainer}>
              <Button
                ref={createButtonRef}
                variant="contained"
                className={classes.createEntity}
                startIcon={<AddIcon />}
                onClick={handleCreateClick}
              >
                <span
                  style={{
                    lineHeight: 'normal',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Create
                </span>
              </Button>
            </div>
          )}

          <NotificationsHeaderItem />

          {!loadingDevtoolsPermission && canAllowDevtools && (
            <LinkButton
              className={classes.linkButtons}
              aria-label="devtools"
              to="devtools"
              data-testid="devtools-button"
              startIcon={<BuildIcon />}
            />
          )}
          <div className={classes.commonContainer}>
            <Button
              ref={settingButtonRef}
              className={classes.linkButtons}
              aria-label="settings"
              startIcon={<Settings />}
              onClick={handleSettingClick}
            />
            {activeMenu === SUBMENU_OPTIONS.setting && (
              <div ref={settingMenuRef} className={classes.supportContainer}>
                <HeaderMenu
                  options={SETTING_SIDEBAR_OPTIONS}
                  onOptionClick={() => setActiveMenu(SUBMENU_OPTIONS.none)}
                  data-testid="header-menu"
                />
              </div>
            )}
          </div>
          <div className={classes.commonContainer}>
            <Button
              ref={supportButtonRef}
              className={classes.linkButtons}
              aria-label="devtools"
              data-testid="help-button"
              startIcon={<HelpIcon />}
              onClick={handleHelpClick}
            />
            {activeMenu === SUBMENU_OPTIONS.support && (
              <div ref={supportMenuRef} className={classes.supportContainer}>
                <HeaderMenu
                  options={SUPPORT_SIDEBAR_OPTIONS}
                  onOptionClick={() => setActiveMenu(SUBMENU_OPTIONS.none)}
                  data-testid="header-menu"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
